// =====================================================================
// Faz N (2026-05-06) — Stok + Ciro Tahminleme
// =====================================================================
// "Ne kadar gelir yapacağım, stok ne durumda" sorusunu cevaplayan tek sayfa.
// 3 panel:
//   1) Ciro Forecast (MTD + günlük ortalama + EOM projection + geçen ay kıyas)
//   2) Stok Riski (30 gün içinde tükenecek 20 ürün)
//   3) Top Velocity (en hızlı satan 10 ürün - stok planlama için)
//
// Endpoint: GET /api/admin/insights/forecast
//
// Not: Linear projeksiyon B2B plastik ambalaj için yeterli — anlamlı
// sezonsallık olduğu zaman (yaz piknik kapakları, kurban kasım poşet) basit
// month-of-year multiplier ile zenginleşebilir (Faz N+).
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, LinearProgress, Avatar
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import API from "../services/api";

const fmtMoney = (v) => v == null ? "—" :
  Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";
const fmtNum = (v, dec = 1) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: dec });
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) : "—";

// Stok riski seviyesi (gün) → renk + etiket
const stockTier = (days) => {
  if (days == null)    return { color: "#94a3b8", label: "Belirsiz" };
  if (days <= 0)       return { color: "#dc2626", label: "BİTTİ" };
  if (days <= 3)       return { color: "#ef4444", label: "KRİTİK" };
  if (days <= 7)       return { color: "#f97316", label: "Acil" };
  if (days <= 14)      return { color: "#f59e0b", label: "Düşük" };
  return                      { color: "#3b82f6", label: "Normal" };
};

export default function Forecast() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    API.get("/api/admin/insights/forecast")
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const rf = data.revenue_forecast;
  const monthProgressPct = rf.days_in_month > 0 ? Math.round((rf.days_passed / rf.days_in_month) * 100) : 0;
  const projectionPct = rf.previous_month_full > 0 ? Math.round((rf.projected_eom / rf.previous_month_full - 1) * 100) : null;
  const projectionColor = projectionPct == null ? "#1565C0"
    : projectionPct >= 0 ? "#10b981" : "#ef4444";

  // Daily trend: chart için max revenue
  const trend = data.daily_trend || [];
  const maxRev = Math.max(...trend.map(d => Number(d.revenue) || 0), 1);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Forecast — Tahminleme</Typography>
          <Typography variant="body2" color="text.secondary">
            Ay sonu ciro projeksiyonu + stok riski + top velocity
          </Typography>
        </Box>
        <Tooltip title="Yenile">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* 1) Ciro Forecast Hero */}
      <Card sx={{ p: 3, borderRadius: 3, mb: 3, background: `linear-gradient(135deg, ${projectionColor}11 0%, ${projectionColor}05 100%)`, border: `2px solid ${projectionColor}33` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CalendarMonthIcon sx={{ color: projectionColor }} />
          <Typography variant="h6" fontWeight={700}>Bu Ay Ciro Tahmini</Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>BUGÜNE KADAR</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0" sx={{ lineHeight: 1.1 }}>
              {fmtMoney(rf.mtd_revenue)}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {rf.mtd_orders} sipariş · {rf.days_passed}/{rf.days_in_month} gün
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>GÜNLÜK ORTALAMA</Typography>
            <Typography variant="h4" fontWeight={800} color="#f59e0b" sx={{ lineHeight: 1.1 }}>
              {fmtMoney(rf.daily_average)}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Bu ayın günlük ciro ortalaması
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>AY SONU PROJEKSİYON</Typography>
            <Typography variant="h4" fontWeight={800} sx={{ color: projectionColor, lineHeight: 1.1 }}>
              {fmtMoney(rf.projected_eom)}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Geçen ay: {fmtMoney(rf.previous_month_full)}
              {projectionPct != null && (
                <Chip
                  size="small"
                  label={`${projectionPct >= 0 ? "+" : ""}%${projectionPct}`}
                  sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: projectionColor, color: "white", fontWeight: 700 }}
                />
              )}
            </Typography>
          </Grid>
        </Grid>
        {/* Ay ilerleme barı */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Ay ilerlemesi: %{monthProgressPct} ({rf.days_passed} gün geçti, {rf.days_in_month - rf.days_passed} gün kaldı)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Kalan beklenen: <strong>{fmtMoney(rf.remaining_to_project)}</strong>
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={monthProgressPct}
            sx={{
              height: 12, borderRadius: 6, bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": { bgcolor: projectionColor, borderRadius: 6 }
            }}
          />
        </Box>
      </Card>

      {/* Daily trend mini-chart */}
      {trend.length > 0 && (
        <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>Son 14 Gün Ciro Trendi</Typography>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 120 }}>
            {trend.map((d, i) => {
              const rev = Number(d.revenue) || 0;
              const heightPct = maxRev > 0 ? (rev / maxRev) * 100 : 0;
              return (
                <Tooltip key={i} title={`${fmtDate(d.day)}: ${fmtMoney(rev)} (${d.orders} sip.)`} placement="top">
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                    <Box sx={{
                      width: "100%", height: `${Math.max(heightPct, 2)}%`,
                      bgcolor: "#1565C0", borderRadius: "4px 4px 0 0",
                      transition: "height 0.3s",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#0D47A1" }
                    }} />
                    <Typography variant="caption" sx={{ fontSize: 10, color: "text.disabled" }}>
                      {fmtDate(d.day)}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Card>
      )}

      {/* 2) Stok Riski */}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ p: 2, bgcolor: "#fef2f2", borderBottom: "1px solid #fee2e2", display: "flex", alignItems: "center", gap: 1 }}>
              <WarningIcon sx={{ color: "#ef4444" }} />
              <Typography variant="h6" fontWeight={700}>Stok Riski (30 gün)</Typography>
              <Box sx={{ flex: 1 }} />
              <Chip label={data.stock_risk.length} size="small" sx={{ bgcolor: "#ef4444", color: "white", fontWeight: 700 }} />
            </Box>
            {data.stock_risk.length === 0 ? (
              <Box sx={{ p: 3 }}><Alert severity="success">30 gün içinde tükenecek ürün yok 🎉</Alert></Box>
            ) : (
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell><strong>Ürün</strong></TableCell>
                    <TableCell align="right"><strong>Stok</strong></TableCell>
                    <TableCell align="right"><strong>Hız (g/gün)</strong></TableCell>
                    <TableCell align="center"><strong>Kalan Gün</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.stock_risk.map(p => {
                    const tier = stockTier(p.days_until_stockout);
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar src={p.image_url} variant="rounded" sx={{ width: 36, height: 36, bgcolor: "#f1f5f9" }}>📦</Avatar>
                            <Box>
                              <Typography fontSize={13} fontWeight={600} sx={{ lineHeight: 1.2 }}>{p.name}</Typography>
                              <Typography variant="caption" color="text.disabled">{p.sku || `#${p.id}`}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(p.stock, 0)}</TableCell>
                        <TableCell align="right">{fmtNum(p.daily_velocity, 2)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={p.days_until_stockout != null ? `${Math.round(p.days_until_stockout)}g` : "—"}
                            size="small"
                            sx={{ bgcolor: tier.color, color: "white", fontWeight: 700, minWidth: 50 }}
                            title={tier.label}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </Grid>

        {/* 3) Top Velocity */}
        <Grid item xs={12} lg={5}>
          <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ p: 2, bgcolor: "#fff7ed", borderBottom: "1px solid #fed7aa", display: "flex", alignItems: "center", gap: 1 }}>
              <LocalFireDepartmentIcon sx={{ color: "#f97316" }} />
              <Typography variant="h6" fontWeight={700}>En Hızlı Satan</Typography>
              <Box sx={{ flex: 1 }} />
              <Chip label="Top 10" size="small" sx={{ bgcolor: "#f97316", color: "white", fontWeight: 700 }} />
            </Box>
            {data.top_velocity.length === 0 ? (
              <Box sx={{ p: 3 }}><Alert severity="info">Henüz velocity verisi yok</Alert></Box>
            ) : (
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Ürün</strong></TableCell>
                    <TableCell align="right"><strong>g/gün</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.top_velocity.map((p, i) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontWeight: 700, color: "#f97316" }}>{i + 1}</TableCell>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={600} sx={{ lineHeight: 1.2 }}>{p.name}</Typography>
                        <Typography variant="caption" color="text.disabled">
                          {p.sku || `#${p.id}`} · stok: {fmtNum(p.stock, 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#f97316" }}>
                        {fmtNum(p.daily_velocity, 2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Yorumlama notu */}
      <Card sx={{ p: 2, borderRadius: 3, mt: 3, bgcolor: "#f8fafc" }}>
        <Typography variant="caption" color="text.secondary">
          💡 <strong>Projeksiyon:</strong> Bu ayın günlük ortalaması × ay sonuna kadar olan gün sayısı.
          Lineer (basit) tahmin — anlamlı sezonsallık yoksa B2B için yeterli.
          Velocity = son 90 gün satılan miktar / 90; days_until_stockout = (mevcut - rezerv) / velocity.
        </Typography>
      </Card>
    </Box>
  );
}
